import { Database } from "../supabase.types";

export type AddressVerified = {
  firm: string | null;
  address: USPSAddress;
  Error?: string;
};

type USPSAddress = {
  streetAddress: string;
  streetAddressAbbreviation: string;
  secondaryAddress: string;
  city: string;
  cityAbbreviation: string;
  state: string;
  postalCode: string;
  province: string;
  ZIPCode: string;
  ZIPPlus4: string;
  urbanization: string;
};

export type ClientErrorType = {
  level: "error" | "info";
  data: {
    message: string;
    stack?: string;
    componentStack?: string | null;
    url: string;
    userAgent: string;
    timestamp: string;
  };
};

export type BucketFile = {
  url: string;
  type: string;
  name: string;
};

export type ReviewType = {
  id: number;
  gender: string;
  name: string;
  image: string;
  location: string;
  text: string;
  product?: string[];
  page: string;
};

export type FileType = {
  fileToUpload: string | File | Blob;
  type: string;
  name: string;
};

export type BeforeAfterType = {
  id: number;
  image: string;
  avatar: string;
  name: string;
};

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type AbTestVariant =
  Database["public"]["Tables"]["ab_test_variants"]["Row"];

export type AbTest = {
  [key: string]: AbTestVariant["name"];
};

export type Languages = Database["public"]["Enums"]["languages"];
export type PatientCoupon =
  Database["public"]["Tables"]["patient_coupons"]["Row"];
export type ProfileTypes = Database["public"]["Tables"]["profiles"]["Row"];
export type PatientProfileType =
  Database["public"]["Tables"]["patients"]["Row"] & {
    profile: Database["public"]["Tables"]["profiles"]["Row"];
  };
export type PaymentInformation =
  Database["public"]["Tables"]["payment_information"]["Row"];
export type ShippingAddress = Omit<
  Database["public"]["Tables"]["shipping_address"]["Row"],
  "patient_id" | "created_at" | "id"
>;
export type States = Database["public"]["Tables"]["states"]["Row"];

export type Tasks = Database["public"]["Tables"]["tasks"]["Row"];

export type IdVerification =
  Database["public"]["Tables"]["id_verification"]["Row"] & {
    online_visit_slug: string;
  };

export type CreateIdVerification = {
  online_visit_slug: string | undefined;
};

export type CreatePatientSubscriptionArgs = {
  price: string;
  status: string;
  coupon_id?: number;
  initial?: boolean;
  invoice_id?: string;
};

export type ReferralCode =
  Database["public"]["Tables"]["referral_codes"]["Row"];

export type PatientSubscription =
  Database["public"]["Tables"]["patient_subscriptions"]["Row"] & {
    product: Product;
    product_category: ProductCategory;
  } & { price: ProductPrice };

export type PatientSubscriptionWithOrder =
  Database["public"]["Tables"]["patient_subscriptions"]["Row"] & {
    product: Product;
    product_category: ProductCategory;
  } & { price: ProductPrice } & { order: Order & { medication: string } };

export type MessageThread = Pick<
  Database["public"]["Tables"]["message_threads"]["Row"],
  "slug"
> & {
  product: Pick<
    Database["public"]["Tables"]["products"]["Row"],
    "name" | "slug" | "display_name"
  >;
};

export type MessagingThread = {
  slug: string;
  last_message: string | null;
  is_active: boolean;
  product_category: {
    name: string;
  };
} & {
  product: Pick<
    Database["public"]["Tables"]["products"]["Row"],
    "name" | "slug" | "display_name"
  >;
};

export type MessageType = Pick<
  Database["public"]["Tables"]["messages"]["Row"],
  "created_at" | "message" | "public_id"
> & {
  sender: Pick<
    Database["public"]["Tables"]["profiles"]["Row"],
    "first_name" | "last_name" | "id" | "preferred_name"
  >;
};
export type MessageThreadWithLastMessage = Pick<MessageThread, "slug"> & {
  last_message: { sent_at: string; message: string };
};
export type CreateMassageArgs = Pick<
  Database["public"]["Tables"]["messages"]["Insert"],
  "message" | "language"
> & {
  thread_slug: string;
};

export type Coupon = Pick<
  Database["public"]["Tables"]["coupons"]["Row"],
  "amount" | "is_active" | "id" | "name"
>;

export type ProductPrice = Omit<
  Database["public"]["Tables"]["prices"]["Row"],
  "id"
> & {
  coupon: Pick<
    Database["public"]["Tables"]["coupons"]["Row"],
    "amount" | "id"
  > | null;
} & {
  product: Pick<Product, "name" | "display_name" | "billing_model" | "slug">;
  product_category: Pick<ProductCategory, "name">;
};

export type ProductCategory = Omit<
  Database["public"]["Tables"]["product_categories"]["Row"],
  "created_at"
>;

export type Product = Omit<
  Database["public"]["Tables"]["products"]["Row"],
  "stripe_product_id"
> & { category: ProductCategory };

export type ProductsWithMedicationForm = Product & {
  form?: MedicationForm;
};

export type Price = Database["public"]["Tables"]["prices"]["Row"];

export type Invoice = Database["public"]["Tables"]["invoices"]["Row"];

export type InvoiceWithPaymentInformation = Invoice & {
  method: PaymentInformation;
  product: Product;
};

export type StripeProductPrice = {
  id: string;
  nickname: string;
  product_name: string;
  unit_amount: number;
  type: string;
  currency: string;
  recurring: {
    interval: string;
    interval_count: number;
  };
  discounted_amount?: number;
};

export type StripeInvoice = {
  id: string;
  status: string;
  created: number;
  amount_paid: number;
  amount_due: number;
  invoice_pdf: string;
  payment_intent: {
    payment_method: {
      card: {
        brand: string;
        last4: string;
      };
    };
  };
  charge: {
    payment_method_details: {
      card: {
        brand: string;
        last4: string;
      };
    };
  };
  description: string;
};

export type PayStripeInvoice = {
  id: string;
  status: string;
  created: number;
  amount_paid: number;
  amount_due: number;
  invoice_pdf: string;
  payment_intent: {
    payment_method: {
      card: {
        brand: string;
        last4: string;
      };
    };
  };
  charge: {
    payment_method_details: {
      card: {
        brand: string;
        last4: string;
      };
    };
  };
  description: string;
};

export type StripeInvoiceError = {
  type: string;
  raw: { message: string };
  code?: string;
  decline_code?: string;
  doc_url?: string;
  statusCode?: number;
  requestId?: string;
};

export type CreateStripeCustomer = {
  id: string;
};

export type EventPayload = {
  name: string;
  data?: {
    [key: string]: unknown;
  };
};

export type PageEventPayload = {
  data?: {
    [key: string]: unknown;
  };
};

export type IntakeQuestion = Question & {
  next_question: string | null;
};

export type OnlineVisitBase = Pick<
  Database["public"]["Tables"]["online_visits"]["Row"],
  "slug" | "status" | "completed_at" | "visit_type" | "id" | "product_id"
>;

export type OnlineVisit = OnlineVisitBase & {
  product: Pick<Product, "name" | "slug">;
  product_category: ProductCategory;
  metadata?: Record<string, unknown>;
  intake: Pick<
    Database["public"]["Tables"]["intakes"]["Row"],
    "first_question"
  > & {
    next: Record<string, string | null>;
  };
  answers?: {
    question_id: string;
    response: AnswerResponse["response"];
  }[];
  questions?: IntakeQuestion[];
};

export type UpdateOnlineVisit = OnlineVisit & { product: string };

export type UpdateVisitArgs = Omit<
  Database["public"]["Tables"]["online_visits"]["Update"],
  "metadata"
> & {
  metadata?: Record<string, unknown>;
};

export type PatientWeight = Omit<
  Database["public"]["Tables"]["weight_tracker"]["Row"],
  "patient_id" | "id"
>;

export type CreatePatientWeightArgs = {
  weight: number;
  height: number;
  date: string;
};

export type UpdatePatientWeightArgs = {
  goal: number;
  date: string;
};

export type AnswerResponse = {
  visitId: string;
  question_id: string;
  question_type: string;
  response: { answer: string[]; details?: string };
  tag?: string;
  score?: number;
};

export type AnswerType = Pick<
  Database["public"]["Tables"]["answers"]["Row"],
  "question_id" | "question_type" | "visit_id"
> & {
  response: AnswerResponse;
};

export type OnlineVisitWithAnswers = { visit: OnlineVisit } & {
  answers: boolean;
} & { next: string };

export type PaymentMethodType = {
  id: string;
  brand: string;
  isDefault: boolean;
  last4: string;
  expiration: string;
};

export type PaymentIntent = {
  client_secret: string;
};

export type ClientSecret = {
  clientSecret: string | null;
};

export type UpdatePaymentArgs = {
  publicId: string;
  updates: Partial<PaymentInformation>;
};

export type SetDefaultPaymentMethodReturn = {
  id: string;
  isDefault: boolean;
};

export type SetDefaultPaymentMethodBody = {
  paymentMethodId: string;
};

export type AnswerQueryArgs = {
  visitId: string;
  questionId: string;
};

export type SubmitAnswerArgs = {
  visitId: string;
  question_id: string;
  question_type: string;
  response: {
    answer: string[];
    details?: string;
  };
  tag?: string;
  score?: number;
};

export type QuestionOptionCondition = {
  depends_on: string;
  answer: string[];
};

export type QuestionOption = {
  value: string;
  label: string;
  slug: string;
  next_question: string | null;
  follow_up: boolean;
  conditions: QuestionOptionCondition | null;
};

export type Question = Omit<
  Database["public"]["Tables"]["questions"]["Row"],
  "options"
> & {
  question_position?: number | null;
  next_question?: string | null;
  options: QuestionOption[] | null;
};

export type Task = Omit<
  Database["public"]["Tables"]["tasks"]["Row"],
  "metadata"
> & {
  metadata: Record<string, unknown> | null;
};

export type TrackCustomer = {
  initial: boolean;
  shippingAddress?: Omit<
    ShippingAddress,
    "patient_id" | "created_at" | "id" | "zip_code"
  >;
};

export type TrackPage = {
  pageUrl: string;
};

export type BucketProps = {
  bucket: string;
  path: string;
};

export enum OrderTypeEnum {
  Initial = "initial",
  Refill = "refill",
  Upsell = "upsell",
}

export type Order = Omit<
  Database["public"]["Tables"]["orders"]["Row"],
  "product_id" | "product_category_id"
>;

export type PatientOrder = Pick<
  Order,
  | "status"
  | "tracking_number"
  | "tracking_url"
  | "delivered_at"
  | "invoice_id"
  | "prescription_id"
> & {
  product: Omit<Product, "category">;
  product_category: ProductCategory;
  details: PrescriptionMedication;
};

export type PrescriptionInstruction =
  Database["public"]["Tables"]["prescription_instructions"]["Row"];

export type Prescription = Database["public"]["Tables"]["prescriptions"]["Row"];

export type PrescriptionMedication = Pick<
  Database["public"]["Tables"]["prescriptions"]["Row"],
  "id"
> & {
  instructions: PrescriptionInstruction[];
  medications: Medications[];
};

export type Encounter = Database["public"]["Tables"]["encounters"]["Row"];
export type EncounterStatus = Database["public"]["Enums"]["encounter_status"];

export type PrescriptionWithProduct = Prescription & {
  product: Pick<Product, "display_name" | "slug">;
  product_category: Pick<ProductCategory, "name">;
};

export type Instruction = Database["public"]["Tables"]["instructions"]["Row"];

export type Medications = Database["public"]["Tables"]["medications"]["Row"];

export type OrderWithPrescription = PatientOrder & {
  prescription: Pick<Prescription, "days_supply" | "dispense_quantity"> & {
    medications: Pick<
      Medications,
      "name" | "dosage" | "strength" | "display_name"
    >[];
  };
};

export type FileStorage = Database["public"]["Tables"]["file_storage"]["Row"];

export type PromoBanner = Pick<
  Database["public"]["Tables"]["banners"]["Row"],
  "text"
>;

export type InstructionTranslations = {
  medication_id: number;
  instruction_label: string;
};

export type OneTimeProduct =
  Database["public"]["Tables"]["one_time_products"]["Row"] & {
    price: Pick<
      ProductPrice,
      | "amount"
      | "stripe_id"
      | "monthly_price"
      | "number_of_months"
      | "amount_limit"
    >;
  } & { product: Pick<Product, "slug"> };

export type OneTimePaymentSuccessArgs = {
  product_id: number;
  product_category_id: number;
  billing_model: ProductBillingModel;
  dispense_quantity?: string;
  online_visit_id?: number;
  days_supply?: number;
  metadata?: {
    dosage?: string;
    invoice_id?: string;
    form?: string;
    prescription_type?: OrderTypeEnum;
  };
  approval_required: boolean;
};

export type AutocompletePrediction = {
  description: string;
  place_id: string;
};

export type AddressDetails = {
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
};

export type MedicationForm = Database["public"]["Enums"]["medication_form"];

export type VisitType = Database["public"]["Enums"]["visit_type"];

export type GetNextPlaceParams = {
  visit_type?: VisitType;
  product_id?: number;
};

export type ProductBillingModel =
  Database["public"]["Enums"]["billing_model_type"];

export type UpdatePatientSubscription =
  Database["public"]["Tables"]["patient_subscriptions"]["Update"];

export enum OrderStatus {
  Created = "created",
  Paid = "paid",
  Unpaid = "unpaid",
  SentToPharmacy = "sent_to_pharmacy",
  Delivered = "delivered",
  Shipped = "shipped",
  Canceled = "canceled",
  Finalized = "finalized", //ready to be charged and sent to pharmacy
}

export type Charge = Database["public"]["Tables"]["charges"]["Row"] & {
  product: Product;
  product_category: ProductCategory;
  price: Price;
};

export type PatientStatusType = Database["public"]["Enums"]["patient_status"];

export type Titration =
  Database["public"]["Tables"]["titration_schedule"]["Row"];

export type ProductVariant =
  Database["public"]["Tables"]["product_variants"]["Row"];

export type PatientRating =
  Database["public"]["Tables"]["patient_ratings"]["Row"];

export type UpdatePatientRating =
  Database["public"]["Tables"]["patient_ratings"]["Update"];

export const ProductCategories = {
  Nausea: "Nausea",
  WeightLoss: "Weight Loss",
  HairLoss: "Hair Loss",
  BirthControl: "Birth Control",
  EmergencyContraception: "Emergency Contraception",
  ErectileDysfunction: "Erectile Dysfunction",
  MentalHealth: "Mental Health",
  SkinCare: "Skin Care",
  Supplements: "Supplements",
  Sleep: "Sleep",
};

export type TaskType = Database["public"]["Enums"]["task_types"];

export type MeasurementTypes = {
  starting: number;
  height: number;
  current: number | null;
};

export type PatientCouponWithInfo = PatientCoupon & {
  coupon: Coupon;
};

export type Batch = Database["public"]["Tables"]["batches"]["Row"];

export type WeightLogger = {
  coupon: PatientCouponWithInfo | null;
  batch: Batch | null;
};

export type Treatment = Database["public"]["Tables"]["treatments"]["Row"];
export type Specialties = Database["public"]["Enums"]["specialties"];

export type EPrescribePharmacy = {
  PharmacyId: number;
  StoreName: string;
  Address1: string;
  Address2: string;
  City: string;
  State: string;
  ZipCode: string;
  PharmacySpecialties: string[];
};

export type TimeSlot = {
  start_at: string;
  provider_availability_id: number;
};

export type Availability = {
  [key: string]: TimeSlot[];
};

export type PatientPreferredPharmacy =
  Database["public"]["Tables"]["patient_preferred_pharmacies"]["Row"];

export type PatientPreferredPharmacyCreate =
  Database["public"]["Tables"]["patient_preferred_pharmacies"]["Insert"];
